import { FocusMonitor, FocusOrigin, FocusableOption } from '@angular/cdk/a11y';
import {
  AfterViewInit,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  Input,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  HostBinding,
} from '@angular/core';
import { Subject, fromEvent, merge } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';

@Component({
  selector: 'button[app-icon-button], a[app-icon-button]',
  templateUrl: './icon-button.component.html',
  styleUrls: ['./icon-button.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'txt-deco',
  },
  inputs: ['disabled', 'color', 'icon', 'size', 'hoverColor'],
})
export class IconButtonComponent implements OnInit, AfterViewInit, OnDestroy, FocusableOption {
  @HostBinding('attr.disabled') get _btnDisabled(): boolean | null {
    return this.disabled || null;
  }
  @HostBinding('class') get _btnColor() {
    return (
      'icon-button-color-' +
      (this.disabled ? 'disabled' : this.isEnter && !!this.hoverColor ? this.hoverColor : this.color)
    );
  }

  @Input() disabled = false;
  @HostBinding('style.--size') @Input() size?: string;
  @Input() icon: string;
  @Input() color: IconButtonColor = 'blue';
  @Input() hoverColor?: IconButtonColor;

  private _destroy$ = new Subject<void>();
  isEnter = false;

  constructor(
    private _el: ElementRef,
    private _focusMonitor: FocusMonitor,
    private _cd: ChangeDetectorRef,
  ) {
    const element = this._el.nativeElement as HTMLButtonElement | HTMLAnchorElement;
    merge(fromEvent(element, 'mouseover'), fromEvent(element, 'touchstart'))
      .pipe(debounceTime(50), takeUntil(this._destroy$))
      .subscribe((_) => {
        this.isEnter = true;
        this._cd.markForCheck();
        this._cd.detectChanges();
      });

    merge(fromEvent(element, 'mouseout'), fromEvent(element, 'touchend'))
      .pipe(debounceTime(50), takeUntil(this._destroy$))
      .subscribe((_) => {
        this.isEnter = false;
        this._cd.markForCheck();
        this._cd.detectChanges();
      });
  }

  focus(origin?: FocusOrigin): void {
    if (origin) {
      this._focusMonitor.focusVia(this._el.nativeElement, origin);
    } else {
      this._el.nativeElement.focus();
    }
  }

  ngOnDestroy(): void {
    this._focusMonitor.stopMonitoring(this._el);
    this._destroy$.next();
    this._destroy$.complete();
  }

  ngAfterViewInit(): void {
    this._focusMonitor.monitor(this._el, true);
  }

  ngOnInit(): void {}
}

type IconButtonColor =
  | 'black'
  | 'white'
  | 'dark-blue'
  | 'blue'
  | 'green'
  | 'red'
  | 'orange'
  | 'disabled'
  | 'dark-grey'
  | 'dark-red'
  | 'dark-green'
  | 'inherit';
